import { useState } from 'react';
import { Wrench, ChevronDown, ChevronRight, Check, Loader2 } from 'lucide-react';
import clsx from 'clsx';
import type { NestedToolCall } from '../../types/chat';
import { formatToolName, formatContent } from './toolFormatters';
import { VideoPlayer, extractClipId, extractVideoUrl } from './VideoPlayer';

interface NestedToolCallBlockProps {
  toolCall: NestedToolCall;
}

/**
 * Compact tool display for nested agent tool calls.
 * Reuses formatting logic from toolFormatters for consistency.
 */
export function NestedToolCallBlock({ toolCall }: NestedToolCallBlockProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const isRunning = toolCall.status === 'running';

  return (
    <div className="mb-2">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={clsx(
          'flex items-center gap-2 w-full px-2 py-1.5 rounded text-xs font-medium',
          'transition-colors border',
          isRunning
            ? 'bg-indigo-50 text-indigo-600 border-indigo-200'
            : 'bg-emerald-50 text-emerald-600 border-emerald-200'
        )}
      >
        {isRunning ? (
          <Loader2 className="w-3 h-3 animate-spin" />
        ) : (
          <Wrench className="w-3 h-3" />
        )}
        <span>{formatToolName(toolCall.name)}</span>
        {!isRunning && <Check className="w-3 h-3 ml-1" />}
        <span className="ml-auto">
          {isExpanded ? (
            <ChevronDown className="w-3 h-3" />
          ) : (
            <ChevronRight className="w-3 h-3" />
          )}
        </span>
      </button>

      {isExpanded && (
        <div className="mt-1 ml-2 p-2 bg-gray-50 rounded text-xs border border-gray-200">
          {/* Compact parameter display */}
          {Object.keys(toolCall.input).length > 0 && (
            <pre className="text-gray-600 overflow-x-auto mb-2 whitespace-pre">
              {formatContent(toolCall.input)}
            </pre>
          )}
          {/* Result */}
          {toolCall.result && (
            <div className="border-t border-gray-200 pt-2 mt-2">
              {/* Video player for extract_video_clip results */}
              {toolCall.name === 'extract_video_clip' && extractClipId(toolCall.result) && (
                <VideoPlayer
                  clipId={extractClipId(toolCall.result)!}
                  videoUrl={extractVideoUrl(toolCall.result) ?? undefined}
                />
              )}
              <div className="font-semibold text-gray-500 mb-1">Result</div>
              <pre className="text-gray-600 overflow-x-auto max-h-32 overflow-y-auto whitespace-pre">
                {formatContent(toolCall.result)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
